import React from "react";
import "../../theme/styles.css";
import AppContainer from "../AppContainer";

const PrivacyPolicyScreen = () => {
  return (
    <AppContainer>
      <h1 style={{ marginBottom: 0 }}>Mucky Duck Privacy Policy</h1>
      <h5 style={{ textAlign: "center" }}>Last updated: Sept 12, 2019</h5>
      <p>
        Welcome to Mucky Duck, a technology platform connecting professional
        auto detailers and washers with customers, accessible via a mobile
        application and website (collectively, the “
        <strong>App</strong>
        ”). The App is owned by SeaHawk Technology Development, LLC (“
        <strong>SeaHawk</strong>
” or “
        <strong>we</strong>
        ”).
      </p>
      <p>
        Below you will find our Privacy Policy (the “
        <strong>Privacy Policy</strong>
        ”), which discusses our practices for handling your personal
        information. Please read the Privacy Policy and our
        {" "}
        <a href="/terms-of-use">Terms of Use</a>
, which govern your use of the
        App (collectively, the “
        <strong>Agreement</strong>
        ”), carefully before you start to use the App.
      </p>
      <p>
        <strong>
          By using the App, you accept and agree to be bound and abide by the
          Agreement. You must accept the terms of this Agreement before you will
          be permitted to register for an account on the App.
        </strong>
        If you do not want to agree to this Agreement, do not use the App and
        delete it from your mobile device.
      </p>
      <p />
      <h2>Privacy Policy</h2>
      <p>
        SeaHawk respects your privacy and is committed to protecting it through
        our compliance with this Privacy Policy (“
        <strong>Privacy Policy</strong>
        ”). This Privacy Policy describes the types of information we may
        collect from users (“
        <strong>Users</strong>
” or “
        <strong>you</strong>
        ”) of our SeaHawk mobile application and website (collectively, the “
        <strong>App</strong>
        ”), and our practices for collecting, using, maintaining, protecting,
        and disclosing that information. “
        <strong>Users</strong>
” includes
        customers who purchase auto-detailing and washing services from
        independent third-party providers (“
        <strong>Customers</strong>
” and “
        <strong>Service Providers,</strong>
”
        respectively).
      </p>
      <p>
        This Privacy Policy applies to information we collect on the App and in
        e-mail, text, and other electronic messages between you and the App. It
        does not apply to information collected by us offline or through any
        other means, or information collected by any other User or third party,
        including any application or content (including advertising) that may
        link to or be accessible from the App.
      </p>
      <p>
        Please read this Privacy Policy carefully to understand our policies and
        practices regarding your information and how we will treat it. If you do
        not agree with our policies and practices, your choice is not to use the
        App. By downloading, accessing or using the App, you agree to this
        Privacy Policy. This Privacy Policy may change from time to time (see
        Changes to this Privacy Policy, below). Your continued use of the App
        after we make changes is deemed to be acceptance of those changes, so
        please check this Privacy Policy periodically for updates.
      </p>
      <h3>I. CHILDREN UNDER THE AGE OF 18</h3>
      <p>
        The App is not intended for children under 18 years of age. No one under
        age 18 may provide any Personal Information (as defined below) to or on
        the App. We do not knowingly collect Personal Information from children
        under 18. If you are under 18, do not use or provide any information on
        this App or through any of its features, or provide any information
        about yourself to us, including your name, address, telephone number, or
        e-mail address. If we learn we have collected or received Personal
        Information from a child under 18 without verification of parental
        consent, we will delete such information.
      </p>
      <h3>II. INFORMATION WE COLLECT ABOUT YOU AND HOW WE COLLECT IT</h3>
      <p>
        When you download, register with, or use the App, we may collect or ask
        you to provide several types of information, including information by
        which you may be personally identified, such as name and e-mail address
        (collectively, “
        <strong>Personal Information</strong>
        ”), and information about your usage of and access to the App.
      </p>
      <p>
        We collect information directly from you when you provide it to us and
        automatically as you navigate through the App. Information collected
        automatically may include information about your Internet connection,
        device, usage details and information collected through cookies and
        other tracking technologies. We may also collect information about you
        from other sources, including third parties.
      </p>
      <h4>A. Information You Provide to Us</h4>
      <p>
        The information we collect through the App includes Personal Information
        that you provide by filling in forms on the App when registering as a
        User; records of your correspondence other Users; transaction details
        regarding your use or provision of services; records of your
        correspondence with us (including e-mail addresses) if you contact us;
        any other content and material you post, submit, publish, display or
        transmit on or through the App (collectively, “
        <strong>User Contributions</strong>
        ”); and information you provide to us if you use the App to communicate
        with other third parties, including through advertisements or marketing
        material on the App.
      </p>
      <p>
        Your User Contributions may be published or displayed on public areas of
        the App, or transmitted to other Users. Your User Contributions are
        posted on the App and transmitted to others at your own risk. Although
        we may allow certain security or privacy features for User
        Contributions, please be aware that no security measures are perfect or
        impenetrable. Additionally, we cannot control the actions of other Users
        with whom you may choose to share your User Contributions. Therefore, we
        cannot and do not guarantee that your User Contributions will not be
        viewed by unauthorized persons.
      </p>
      <h4>B. Automatic Information Collection</h4>
      <p>
        When you download and as you navigate through and interact with the App,
        we may use automatic data collection technologies to collect certain
        information about your browsing actions and patterns. Such information
        provides us with statistical data that helps us improve the App and
        offer more personalized service by estimating our audience size and
        usage patterns, storing information about User preferences, and
        recognizing Users when they return to the App. Such information may
        include details of your visits to the App, including traffic data, logs,
        and other communication data and resources that you access and use on
        the App. We may collect information about your mobile device and
        Internet connection, including your device’s unique device identifier,
        the GPS location of your device, Internet Protocol (IP) address,
        operating system, and mobile network information, and the device’s
        telephone number. If you do not want us to collect this information, do
        not download the App or delete it from your device.
      </p>
      <h4>C. Information Collection and Tracking Technologies</h4>
      <p>
        The technologies we use for automatic information collection may include
        cookies, web beacons, and other tracking technologies. A cookie is a
        small file placed on your device. It may be possible to refuse to accept
        mobile cookies by activating the appropriate setting on your device.
        Cookies can help recognize your browser across different pages of the
        App and browsing sessions and help us improve your experience on the App
        by allowing us to store your information so that we recognize you when
        you log in to the App and help us prevent identity theft and fraud with
        User authentication data. Pages of the App and our e-mails may include
        small electronic files known as web beacons (also referred to as clear
        gifs, pixel tags, and single-pixel gifs) that permit us, for example, to
        count Users who have visited those pages or opened an e-mail, and for
        other related App statistics.
      </p>
      <p>
        The App may use analytics and crash-reporting software (e.g., Google
        Analytics or Fabric) in order to better understand how our Users
        interact with the App and to drive improvements of the User experience.
        The analytics and crash-reporting software collect information about
        pages visited on an app or website, how often users visit such app or
        website, and what other website users visited prior to coming to such
        website. We do not combine the information collected through the use of
        analytics software with your Personal Information.
      </p>
      <h4>D. Information We Collect from Third Parties</h4>
      <p>
        As part of the registration process, Service Providers must provide
        Personal Information, including banking information, directly to a
        third-party payment processing service (“
        <strong>Payment Processor</strong>
        ”) so that they may collect compensation for services rendered. Service
        Providers may also be asked by a Payment Processor to answer
        knowledge-based questions for authentication. Any information you
        provide to a Payment Processor will be subject to that processor’s terms
        of use and privacy policy, so please review such terms prior to
        transmitting your Personal Information.
      </p>
      <p>
        We may collect Personal Information about your requests and interactions
        with us and other Users and our service providers (e.g., a Payment
        Processor or a service provider used to reset your password), and obtain
        information about you from other third-party sources, such as data
        brokers, commercial sources, and public databases.
      </p>
      <h3>III. HOW WE USE YOUR INFORMATION</h3>
      <h4>A. App Services and Requests</h4>
      <p>
        We use information that we collect about you or that you provide to us,
        including any Personal Information: (i) to present the App and its
        contents to you; (ii) to provide you with information, products, or
        services that you request from us; (iii) to fulfill any other purpose
        for which you provide such information; (iv) to carry out our
        obligations and enforce our rights arising from any obligations and
        enforce our rights arising from any contracts entered into between you
        and us, including for billing and collection, if applicable; (v) to
        allow you to participate in interactive features on the App; (vi) to
        understand and better serve Users and improve our products, including,
        for example, customer service, research and development, and compliance;
        (vii) to enable us to display advertisements to our advertisers’ target
        audiences; (viii) in any other way we may describe when you provide the
        information; and (ix) for any other purpose with your consent.
      </p>
      <h4>B. Information Sharing</h4>
      <p>
        We may disclose aggregated information about our Users without
        restriction. We may disclose Personal Information that we collect or you
        provide as described in this Privacy Policy: (i) to contractors, service
        providers, and other third parties we use to support our business; (ii)
        to a buyer or other successor in the event of a merger, divestiture,
        restructuring, reorganization, dissolution, or other sale or transfer of
        some or all of our assets, whether as a going concern or as part of
        bankruptcy, liquidation, or similar proceeding, in which Personal
        Information held by us about our Users is among the assets transferred;
        (iii) where we believe such disclosure is appropriate to identify,
        combat, and address fraud or other illegal activity and to improve the
        security of the App environment; (iv) to fulfill the purpose for which
        you provide it; (v) for any other purpose disclosed by us when you
        provide the information; and (vi) with your consent.
      </p>
      <p>
        We may also disclose your Personal Information: (x) to comply with any
        court order, law, or legal process, including to respond to any
        government or regulatory request; (y) to enforce or apply terms of
        agreements, including for billing and collection purposes; and (z) if we
        believe disclosure is necessary or appropriate to protect the rights,
        property, or safety of us, our customers, or others.
      </p>
      <h3>IV. DATA SECURITY AND INFORMATION STORAGE</h3>
      <p>
        We have implemented reasonable measures designed to secure your Personal
        Information from accidental loss and unauthorized access, use,
        alteration, and disclosure. Unfortunately, the transmission of
        information via the Internet is not completely secure. Although we do
        our best to protect your Personal Information, we cannot guarantee the
        security of your Personal Information transmitted to the App. Any
        transmission of Personal Information is at your own risk.
      </p>
      <p>
        Information regarding Users may be maintained, processed, and stored by
        us and our authorized affiliates and service providers unless you wish
        to have your Personal Information deleted and have communicated that
        wish to us. However, we may retain necessary information after account
        deletion or such deletion request to comply with any court order, law,
        or legal process, which includes responses to any government or
        regulatory requests; to enforce or apply this Agreement; and if we
        believe disclosure is necessary or appropriate to protect the rights,
        property, or safety of SeaHawk, its Users, or others.
      </p>
      <h3>V. DATA RETENTION</h3>
      <p>
        We may retain your Personal Information for as long as legitimate and to
        the extent required to provide Services, and in accordance with the
        instructions of our Users who are the controllers of the Personal
        Information we collect and/or process. This includes, without
        limitation, as reasonably necessary to comply with our legal obligations
        and/or protect our legitimate interests, and with respect to data used
        for marketing activities, for as long as we have User consent therefor.
        We then either delete such information from our systems or anonymize it
        without further notice to you.
      </p>
      <h3>VI. CHOICES ABOUT HOW WE USE AND DISCLOSE YOUR INFORMATION</h3>
      <p>
        We strive to provide you with choices regarding the Personal Information
        you provide to us. We have created mechanisms to provide you with the
        following control over your information:
      </p>
      <p>
        <ul>
          <li>
            You can set your browser to refuse all or some browser cookies, or
            to alert you when cookies are being sent. You may also control when
            the App collects information about or tracks your device’s GPS
            location through your device settings. If you disable or refuse
            cookies or GPS location services, please note that some parts of the
            App may then be inaccessible or not function properly.
          </li>
          <li>
            You can review and change your Personal Information by logging in to
            the App and visiting your account profile page. You may also send us
            an e-mail at
            {" "}
            <a href="mailto:support@seahawktech.com">support@seahawktech.com</a>
            {" "}
            to request access to, correct, or delete any Personal Information
            that you have provided to us. We cannot delete your Personal
            Information except by also deleting your User account. We may not
            accommodate a request to change information if we believe the change
            would violate any law or legal requirement or cause the information
            to be correct.
          </li>
        </ul>
      </p>
      <h3>VII. CHANGES TO THIS PRIVACY POLICY</h3>
      <p>
        We may change this Privacy Policy from time to time. If we make changes,
        we will notify you by revising the date at the top of the Privacy Policy
        and, in some cases, we may provide you with additional notice (such as
        adding a statement to our homepage or sending you an e-mail). We
        encourage you to review the Privacy Policy whenever you access the App
        to stay informed about our information practices.
      </p>
      <h3>VIII. YOUR CALIFORNIA PRIVACY RIGHTS</h3>
      <p>
        California’s “
        <strong>Shine the Light</strong>
” law (Civil Code Section
        1798.83) permits Users of the App that are California residents to
        request certain information regarding our disclosure of Personal
        Information to third parties for their direct marketing purposes. To
        make such a request, please send an e-mail to
        {" "}
        <a href="mailto:support@seahawktech.com">support@seahawktech.com</a>
.
      </p>
      <h3>IX. USERS OUTSIDE THE UNITED STATES</h3>
      <p>
        The App is intended only for Users in the United States. If you access
        or use the App from outside the United States, information you provide
        or that we obtain as a result of such use may be processed and
        transferred to the United States, and may be subject to United States
        laws. By using the App, you consent to the collection, transfer,
        storage, and processing of your information in and to the United States.
      </p>
      <h3>X. CONTACT US</h3>
      <p>
        To ask questions or comments about this Privacy Policy and our privacy
        practices, please contact us at
        {" "}
        <a href="mailto:support@seahawktech.com">support@seahawktech.com</a>
.
      </p>
    </AppContainer>
  );
};

export default PrivacyPolicyScreen;



// WEBPACK FOOTER //
// ./src/screens/general/PrivacyPolicyScreen.js